import type { ChartData, ChartOptions } from 'chart.js';

import { getCountSeriesYAxisMax } from 'utils/reportChartAxis';
import type { ReportRefItemType } from 'types/api/quizReportRefResponseType';
import { formatRateToPercent, getChannelAcquiredCount, getChannelAcquisitionRate } from 'utils/app';

export type ChartType =
  | 'participatedCount'
  | 'mailAddressCount'
  | 'mailAddressAcquisitionRate'
  | 'answeredCount'
  | 'answeredRate';

const BASE_COLORS: Record<ChartType, string> = {
  participatedCount: '#7d7bd1',
  mailAddressCount: '#fec53d',
  mailAddressAcquisitionRate: '#4ad9d9',
  answeredCount: '#a855f7',
  answeredRate: '#ff738d',
};

const BACKGROUND_COLORS: Record<ChartType, string> = {
  participatedCount: '#eef3fe',
  mailAddressCount: '#fff3d9',
  mailAddressAcquisitionRate: '#dcf7f7',
  answeredCount: '#f3e8ff',
  answeredRate: '#ffe0e6',
};

export function getBaseColor(chartType: ChartType): string {
  return BASE_COLORS[chartType];
}

export function getBackgroundColor(chartType: ChartType): string {
  return BACKGROUND_COLORS[chartType];
}

/**
 * chartType に応じてレポートから数値配列を抽出します。
 * rate 系は formatRateToPercent 経由になるため
 * null が返る可能性がありますが、現行挙動と同一のため number[] で型付けします。
 */
export function extractChartValues(
  reports: ReportRefItemType[],
  chartType: ChartType,
  lineFlag: boolean
): number[] {
  switch (chartType) {
    case 'participatedCount':
      return reports.map(r => r.participatedCount);
    case 'mailAddressCount':
      return reports.map(r => getChannelAcquiredCount(lineFlag, r.mailAddressCount, r.lineCount));
    case 'answeredCount':
      return reports.map(r => r.answeredCount);
    case 'mailAddressAcquisitionRate':
      return reports.map(r =>
        formatRateToPercent(
          getChannelAcquisitionRate(
            lineFlag,
            r.mailAddressAcquisitionRate,
            r.lineAddressAcquisitionRate
          )
        )
      );
    case 'answeredRate':
      return reports.map(r => formatRateToPercent(r.answeredRate));
  }
}

export function buildChartData(
  reports: ReportRefItemType[],
  chartType: ChartType,
  lineFlag: boolean
): ChartData<'line'> {
  const labels = reports.map(r => r.label);
  const data = extractChartValues(reports, chartType, lineFlag);
  const baseColor = getBaseColor(chartType);
  const backgroundColor = getBackgroundColor(chartType);

  return {
    labels,
    datasets: [
      {
        data,
        fill: true,
        backgroundColor,
        borderColor: baseColor,
        pointBackgroundColor: baseColor,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: baseColor,
        tension: 0.1,
      },
    ],
  };
}

export function buildChartOptions(values: number[], chartType: ChartType): ChartOptions<'line'> {
  const maxVal = Math.max(...values);
  const isRate = chartType === 'mailAddressAcquisitionRate' || chartType === 'answeredRate';

  return {
    plugins: {
      legend: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 5,
      },
    },
    scales: isRate
      ? {
          y: {
            max: 100,
            min: 0,
          },
        }
      : {
          y: {
            max: getCountSeriesYAxisMax(maxVal),
            min: 0,
          },
        },
  };
}
